<script setup lang="ts">
  import { computed, toRef } from "vue";
  import { Tooltip } from "@/components/ui/tooltip";
  import { COMPLETION_COLORS } from "@/lib/chartPalettes";
  import { percent, useCompletionCards, type CompletionCard } from "./completionMetrics";

  const props = defineProps<{ cards: CompletionCard[]; starts: number }>();

  const bars = useCompletionCards(toRef(props, "cards"), toRef(props, "starts"));

  const LABEL_MIN = 5;
  const GRID = [100, 75, 50, 25, 0];

  const legend = computed(() => [
    { label: "OK", color: COMPLETION_COLORS.ok },
    { label: "Drop", color: COMPLETION_COLORS.drop },
    { label: "Screenout", color: COMPLETION_COLORS.screenout },
    { label: "Not shown (condition)", color: COMPLETION_COLORS.conditional },
  ]);

  const segmentHeight = (part: number, bar: { reach: number; notShown: number }) => {
    const total = bar.reach + bar.notShown;
    return total > 0 ? `${(part / total) * 100}%` : "0%";
  };
</script>

<template>
  <div>
    <div class="flex items-start justify-between gap-4">
      <h2 class="heading-section">Respondents reaching each card</h2>
      <ul class="flex items-center gap-4 text-13 text-foreground-secondary">
        <li
          v-for="entry in legend"
          :key="entry.label"
          class="flex items-center gap-1.5">
          <span
            class="size-2.5 rounded-xs"
            :style="{ backgroundColor: entry.color }" />
          {{ entry.label }}
        </li>
      </ul>
    </div>

    <div class="mt-8 flex gap-3">
      <div class="flex h-72 w-9 shrink-0 flex-col justify-between text-right text-3xs text-muted-foreground">
        <span
          v-for="tick in GRID"
          :key="tick"
          class="-translate-y-1/2 leading-none first:translate-y-0 last:-translate-y-full">
          {{ tick }}%
        </span>
      </div>

      <div class="min-w-0 flex-1 overflow-x-auto pb-16">
        <div class="relative h-72">
          <div
            v-for="tick in GRID"
            :key="tick"
            class="absolute inset-x-0 border-t border-dashed border-border"
            :style="{ top: `${100 - tick}%` }" />

          <div class="relative flex h-full items-end gap-1 pr-1">
            <Tooltip
              v-for="bar in bars"
              :key="bar.token"
              :delay-duration="100">
              <div class="relative h-full min-w-4 max-w-8 flex-1">
                <div
                  class="absolute inset-x-0 bottom-0 flex flex-col overflow-hidden rounded-t-xs"
                  :style="{ height: `${bar.reach + bar.notShown}%` }">
                  <div
                    v-if="bar.notShown > 0"
                    :style="{ height: segmentHeight(bar.notShown, bar), backgroundColor: COMPLETION_COLORS.conditional }" />
                  <div
                    v-if="bar.screenout > 0"
                    :style="{ height: segmentHeight(bar.screenout, bar), backgroundColor: COMPLETION_COLORS.screenout }" />
                  <div
                    v-if="bar.drop > 0"
                    :style="{ height: segmentHeight(bar.drop, bar), backgroundColor: COMPLETION_COLORS.drop }" />
                  <div
                    class="min-h-0 flex-1"
                    :style="{ backgroundColor: COMPLETION_COLORS.ok }" />
                </div>
                <div
                  class="absolute inset-x-0 flex flex-col items-center text-3xs font-semibold leading-none"
                  :style="{ bottom: `calc(${bar.reach}% + 3px)` }">
                  <span
                    v-if="bar.screenoutOfCard >= LABEL_MIN"
                    :style="{ color: COMPLETION_COLORS.screenout }">
                    {{ bar.screenoutOfCard }}%
                  </span>
                  <span
                    v-if="bar.dropOfCard >= LABEL_MIN"
                    class="text-primary">
                    {{ bar.dropOfCard }}%
                  </span>
                </div>
                <span
                  class="absolute top-full left-1/2 mt-2 max-w-16 origin-top-right -translate-x-full -rotate-45 truncate text-3xs text-muted-foreground">
                  {{ bar.name }}
                </span>
              </div>

              <template #content>
                <div class="max-w-xs text-xs">
                  <div class="mb-1">
                    <span class="font-semibold">{{ bar.name }}</span>
                    <span class="ml-1 text-muted-foreground">{{ bar.type }}</span>
                  </div>
                  <div
                    v-if="bar.title"
                    class="mb-2 border-b pb-2 text-2xs leading-tight text-muted-foreground">
                    {{ bar.title }}
                  </div>
                  <div class="mb-1 text-muted-foreground">N = {{ bar.n }} ({{ percent(bar.reach) }} of starts)</div>
                  <div
                    v-if="bar.nNotShown > 0"
                    class="mb-1 text-muted-foreground">
                    Not shown to {{ bar.nNotShown }} still in the survey ({{ percent(bar.notShown) }} of starts)
                  </div>
                  <div class="py-0.5">
                    OK: <strong>{{ bar.nOk }}</strong> ({{ percent(bar.ok) }} of starts)
                  </div>
                  <div
                    v-if="bar.nDrop > 0"
                    class="py-0.5">
                    Drop: <strong>{{ bar.nDrop }}</strong> ({{ bar.dropOfCard }}% of the card)
                  </div>
                  <div
                    v-if="bar.nScreenout > 0"
                    class="py-0.5">
                    Screenout: <strong>{{ bar.nScreenout }}</strong> ({{ bar.screenoutOfCard }}% of the card, net
                    {{ bar.netScreenoutOfCard }}%)
                    <div
                      v-if="bar.answers.length"
                      class="mt-1 ml-2 space-y-0.5 border-l border-muted pl-2 text-3xs text-muted-foreground">
                      <div
                        v-for="answer in bar.answers"
                        :key="answer.title">
                        {{ answer.title }}: <strong>{{ answer.nScreenout }}</strong> ({{ answer.screenoutOfCard }}%)
                      </div>
                    </div>
                  </div>
                </div>
              </template>
            </Tooltip>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
